/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';
import classnames from 'classnames';


import {
    IBuyboxAddToCartViewProps,
    IBuyboxAddToOrderTemplateViewProps,
    IBuyboxData,
    IBuyboxKeyInPriceViewProps,
    IBuyboxProductConfigureDropdownViewProps,
    IBuyboxProductConfigureViewProps,
    IBuyboxProductQuantityViewProps,
    IBuyboxProps,
    IBuyboxShopSimilarLookViewProps
} from '@msdyn365-commerce-modules/buybox';
import { IBuyboxViewProps } from './buybox';
import { IBuyboxFindInStoreViewProps } from './components/buybox-find-in-store';

import { attrNames } from '../../utilities/global-constants';
//@CUSTOM: PDFMake imports
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';

import { format, ProductAvailableQuantity, SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import { getEstimatedAvailabilityAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import { ICoreContext } from '@msdyn365-commerce/core';
import { SingleProductEvent } from '../../utilities/analytics/analytics-events';
import { publish } from '../../utilities/analytics/analytics-dispatcher';

import { clsHelper } from '../../utilities/class-name-helper';

//----------------------------------------------------------
// @CUSTOM: Class name utility for customizations that
// want to use the same base class
//----------------------------------------------------------
const BASE_CLASS = 'msc-product';
const cls = (fragment?: string) => clsHelper(BASE_CLASS, fragment);
let sentAnalytics = false;
pdfMake.vfs = pdfFonts.pdfMake.vfs;
const BuyboxView: React.FC<IBuyboxViewProps & IBuyboxProps<IBuyboxData>> = props => {
    const {
        ModuleProps,
        MediaGalleryContainerProps,
        ProductInfoContainerProps,
        addToCart,
        addToOrderTemplate,
        configure,
        findInStore,
        quantity,
        price,
        title,
        rating,
        inventoryLabel,
        shopSimilarLook,
        keyInPrice,
        shopSimilarDescription,
        unitOfMeasure,
        productComparisonButton,
        data
    } = props;

    // @CUSTOM: PDP view analytics
    if (!sentAnalytics && typeof window !== 'undefined') {

        const product = props.data.product.result;

        setTimeout(() => {
            // @CUSTOM: send pdpview event here to ensure subscribe has fired
            if (product && !sentAnalytics) {
                sentAnalytics = true;

                const productPrice = props.data.productPrice.result;

                const analyticsProductData: SingleProductEvent = {
                    itemID: product.ItemId || '',
                    name: product.Name || '',
                    discount: productPrice?.DiscountAmount,
                    variant: '',
                    quantity: 1,
                    price: productPrice?.BasePrice || product.Price,
                    currency: props.context.request.channel?.Currency || 'USD'
                };
                publish('productDetailView', analyticsProductData);
            }
        }, 0);
    }

    const [productAvailability, setProductAvailability] = React.useState<number>(0);
    const productWeight = data.productSpecificationData?.result?.find(attribute => {
        return attribute.Name === attrNames.attributeValueWeight;
    })?.FloatValue?.toString() || '';
    const productLength = data.productSpecificationData?.result?.find(attribute => {
        return attribute.Name === attrNames.attributeValueLength;
    })?.FloatValue?.toString() || '';
    const productWidth = data.productSpecificationData?.result?.find(attribute => {
        return attribute.Name === attrNames.attributeValueWidth;
    })?.FloatValue?.toString() || '';

    const isItemOverweight = data.productSpecificationData?.result?.find(attribute => {
        return attribute.Name === attrNames.attributeValueItemLTL;
    })?.BooleanValue === true;

    const isItemNLA = data.productSpecificationData?.result?.find(attribute => {
        return attribute.Name === attrNames.attributeValueItemNotAvailable;
    })?.BooleanValue === true;

    const brand = data.productSpecificationData?.result?.find(attribute => {
        return attribute.Name === attrNames.attributeValueBrand;
    })?.TextValue || '';

    const productType = data.productSpecificationData?.result?.find(attribute => {
        return attribute.Name === attrNames.attributeValueProductType;
    })?.TextValue || '';

    const promoMessage = data.productSpecificationData?.result?.find(attribute => {
        return attribute.Name === attrNames.attributeValueWebPromoMessage;
    })?.TextValue || '';

    const leadTime = data.productSpecificationData?.result?.find(attribute => {
        return attribute.Name === attrNames.attributeValueWebProductLeadTime;
    })?.IntegerValue || 0;

    const addToCartBackorderMessage = props.context.app.config.rsmAddToCartBackorderMessage;
    const addToCartBackorderMessageLeadTime = props.context.app.config.rsmAddToCartBackorderMessageLeadTime;

    let isModel = false;
    if (productType === 'Model') {
        isModel = true;
    }

    React.useEffect(() => {
        async function getProductAvailability(product: SimpleProduct, context: ICoreContext) {
            const unformattedProductAvailability = await getEstimatedAvailabilityAsync({ callerContext: context.actionContext }, { ProductIds: [product.RecordId], DefaultWarehouseOnly: true, QuantityUnitTypeValue: 2 });
            const productAvailability = unformattedProductAvailability.ProductWarehouseInventoryAvailabilities?.map((product) => {
                return { ProductId: product.ProductId, AvailableQuantity: product.PhysicalAvailable };
            }) as ProductAvailableQuantity[];
            const numAvailable = (productAvailability.find(allAvailabilities => {
                return allAvailabilities.ProductId === product.RecordId;
            })?.AvailableQuantity);
            if (numAvailable) {
                setProductAvailability(numAvailable);
            }
            else {
                setProductAvailability(0);
            }
        }
        if (data.product?.result) {
            getProductAvailability(data.product.result, props.context);
        }
    });
    function timeStringToDate(timeString: string) {
        const [hours, minutes, seconds] = timeString.split(':').map(Number);
        const date = new Date();
        date.setHours(hours, minutes, seconds, 0);
        return date;
    }
    const shipBy = props.context.app.config.shipsIn || '17:00:00';
    const date = new Date();
    //@CHANGE: Updated the shipby time to always be set in ET
    const easternTime = date.toLocaleTimeString('en-US', { hour12: false, timeZone: 'America/New_York' });
    const shipByDate = timeStringToDate(shipBy);
    const userDate = timeStringToDate(easternTime);
    //const currentProduct = data.product.result;
    const hourDifference = Math.round((shipByDate.getTime() - userDate.getTime()) / (1000 * 60 * 60));

    const hasSlots = !!props.slots?.previousPartsView?.length;
    const url = props.context.request.url.requestUrl.origin;
    return (
        <Module {...ModuleProps} className={classnames(ModuleProps.className, { 'model': isModel })}>
            <Node {...MediaGalleryContainerProps}>{props.mediaGallery}</Node>
            <Node {...ProductInfoContainerProps}>
                {title}
                {data.product.result && rsmRenderProduct(data.product.result, isModel)}
                {!isItemNLA && rsmRenderLeadTime(leadTime, productType, productAvailability, addToCartBackorderMessage, addToCartBackorderMessageLeadTime)}
                {promoMessage && rsmRenderPromoMessage(promoMessage)}
                {!isModel && !isItemNLA && price}
                {!isModel && unitOfMeasure}
                {props.bulkPurchaseLink}
                {rating}
                <div className='ms-buybox__action-group'>
                    {configure && _renderConfigure(configure)}
                    {keyInPrice && _renderKeyInPrice(keyInPrice)}
                    <span>
                        {!isItemOverweight && !isItemNLA && quantity && !isModel && _renderQuantity(quantity)}
                        {!isItemNLA && rsmRenderAvailability(productType, productAvailability)}
                    </span>
                    {inventoryLabel}
                    {(!isItemOverweight && !isItemNLA) && !isModel && (productAvailability !== undefined && productAvailability > 0) && rsmDisplayShipsTodayHours(hourDifference, props.resources.shipWithinMessage)}
                    {(!isItemOverweight && !isItemNLA && addToCart) && !isModel && _renderAddToCart(addToCart)}
                    {(!isItemOverweight && !isItemNLA && !isModel) && addToOrderTemplate && _renderAddToOrderTemplate(addToOrderTemplate)}
                </div>
                {!isItemNLA && isItemOverweight && rsmRenderContactMessage(props.config.contactHobartOverweightMessage || '')}
                {isItemNLA && rsmRenderContactMessageNLA(props.config.contactHobartCustomerCare || '')}
                {findInStore && _renderFindInStore(findInStore)}
                {_renderSocialShare(props.slots && props.slots.socialShare)}
                {productComparisonButton}
                {shopSimilarLook && _renderShopSimilarItem(shopSimilarLook)}
                {shopSimilarDescription && _renderShopSimilarItem(shopSimilarDescription)}
            </Node>
            <div className='ms-buybox__extra-details'>
                {data.product.result && rsmRenderPDPPreviewButton(data.product.result, props.resources.genuinePartsLabel, productAvailability, productWeight, productWidth, productLength, productType)}
                {data.product.result && !isModel && rsmRenderPartInfo(data.product.result, brand, url)}
                {hasSlots && props.slots.previousPartsView && rsmRenderPreviousParts(props.slots.previousPartsView)}
                {hasSlots && props.slots.alternatePartsView && rsmRenderAlternateParts(props.slots.alternatePartsView)}
            </div>
        </Module>
    );
};

const rsmRenderLeadTime = (leadTime: number, productType: string, productAvailability: number, addToCartBackorderMessage: string, addToCartBackorderMessageLeadTime: string): JSX.Element  => {
    if (productType !== 'Model' && (productAvailability !== undefined && productAvailability <= 0)) {
        return (
            <div className={cls('lead-time')}>
                <span className={cls('lead-time')}>{leadTime ? format(addToCartBackorderMessageLeadTime, leadTime) : addToCartBackorderMessage}</span>
            </div>
        );
    }
    else{
        return <></>;
    }
};
const rsmRenderAvailability = (productType: string, productAvailability: number): JSX.Element => {
    if (productType === 'Model') {
        return <></>;
    }
    if (productAvailability && productAvailability > 0) {
        return (
            <div className={cls('availability')}>
                <span className={cls('availability-label')}>Availability: </span>
                <span className={classnames(cls('availability-value'), 'in-stock')}>In stock</span>
            </div>
        );
    } else {
        return (
            <div className={cls('availability')}>
                <span className={cls('availability-label')}>Availability: </span>
                <span className={classnames(cls('availability-value'), 'backorder')}>Backorder</span>
            </div>
        );
    }
};
const rsmRenderPromoMessage = (message: string): JSX.Element => {
    return (
        <p className='ms-buybox__product-promo'>{message}</p>
    );
};
const rsmRenderPreviousParts = (itemList: React.ReactNode[]): JSX.Element => {
    return (
        <div className='ms-buybox__parts'>
            {itemList[0]}
        </div>
    );
};
const rsmRenderAlternateParts = (itemList: React.ReactNode[]): JSX.Element => {
    return (
        <div className='ms-buybox__parts'>
            {itemList[0]}
        </div>
    );
};
const rsmRenderProduct = (product: SimpleProduct, isModel: boolean): JSX.Element => {
    return (
        <div className='ms-buybox__item-id'>
            <span className='ms-buybox__item-id-label'>{!isModel ? 'Part #: ' : 'Model #: '}</span>
            <span className='ms-buybox__item-id-value'>{product.ItemId}</span>
        </div>
    );
};

const rsmRenderPartInfo = (product: SimpleProduct, brand: string, url: string): JSX.Element => {
    return (
        <div className='ms-buybox__parts-info'>
            <div className='ms-buybox__parts-info-item'>
                <span className='ms-buybox__parts-info-label'>Units: </span>
                <span className='ms-buybox__parts-info-value'>{product.DefaultUnitOfMeasure}</span>
            </div>
            <div className='ms-buybox__parts-info-item'>
                <span className='ms-buybox__parts-info-label'>Brand: </span>
                <span className='ms-buybox__parts-info-value'>{brand}</span>
            </div>
        </div>
    );
};

const rsmRenderPDPPreviewButton = (product: SimpleProduct, genuinePartsLabel: string, productAvailability: number, weight: string, width: string, length: string, productType: string): JSX.Element => {
    if (productType !== 'Model') {
        return <></>;
    }
    return (
        <div className='ms-buybox__preview'>
            <button className='ms-buybox__preview-button' onClick={(e) => { rsmGeneratePDPPreview(product, genuinePartsLabel, productAvailability, weight, width, length); }}>Print Preview</button>
        </div>
    );
};
const rsmGeneratePDPPreview = (product: SimpleProduct, genuinePartsLabel: string, productAvailability: number, weight: string, width: string, length: string): void => {
    const checkImageExists = (url: string, callback: (exists: boolean) => void) => {
        const img = new Image();
        img.onload = () => callback(true);
        img.onerror = () => callback(false);
        img.src = url;
    };
    checkImageExists(product.PrimaryImageUrl || '', (exists) => {
        const imageUrl = exists ? product.PrimaryImageUrl : 'https://images-us-prod.cms.commerce.dynamics.com/cms/api/rvzqjtmqwm/imageFileData/MA2qSR?pubver=1';
        const docDefinition: any = {
            content: [
                {
                    columns: [
                        {
                            image: 'hobartLogo', // Check this image reference
                            width: 100,
                            margin: [0, 0, 0, 20]
                        },
                        {
                            text: genuinePartsLabel,
                            style: 'header',
                            alignment: 'right',
                            margin: [0, 10, 0, 20]
                        }
                    ]
                },
                {
                    columns: [
                        {
                            image: 'productImage', // Check this image reference
                            width: 200,
                            height: 200,
                            alignment: 'center',
                            margin: [0, 0, 0, 10]
                        },
                        {
                            stack: [ // Use stack to group text-based content
                                { text: product.Name, style: 'productTitle', margin: [0, 0, 0, 10] },
                                { text: `Item No. ${product.ItemId}`, style: 'subheader', margin: [0, 0, 0, 10] },
                                { text: 'Price', style: 'subheader' },
                                { text: `${product.BasePrice} Price Per Piece excl. tax`, style: 'price', margin: [0, 0, 0, 10] },
                                { text: `Availability: ${productAvailability ? productAvailability : 'Out of Stock'}`, style: 'availability', margin: [0, 0, 0, 20], color: 'red' },
                                {
                                    text: productAvailability ? '' : 'Please call Customer Service for availability.',
                                    style: 'customerService',
                                    alignment: 'center',
                                    margin: [0, 10, 0, 10],
                                    decoration: 'underline'
                                }
                            ]
                        }
                    ]
                },
                // Specifications Table
                {
                    text: 'SPECIFICATIONS',
                    style: 'specificationsHeader',
                    margin: [0, 20, 0, 10]
                },
                {
                    table: {
                        widths: ['*', '*', '*', '*'],
                        body: [
                            [attrNames.attributeValueWeight, attrNames.attributeValueLength, attrNames.attributeValueWidth, 'Height (inch)'],
                            [weight, length, width, '0'] // Replace with actual product specs if needed
                        ]
                    },
                    layout: 'lightHorizontalLines'
                }
            ],
            styles: {
                header: {
                    fontSize: 12,
                    bold: true
                },
                productTitle: {
                    fontSize: 22,
                    bold: true
                },
                subheader: {
                    fontSize: 14
                },
                price: {
                    fontSize: 16,
                    bold: true
                },
                availability: {
                    fontSize: 14,
                    bold: true
                },
                customerService: {
                    fontSize: 12,
                    italics: true
                },
                specificationsHeader: {
                    fontSize: 16,
                    bold: true
                }
            },
            images: {
                hobartLogo: 'https://images-us-prod.cms.commerce.dynamics.com/cms/api/rvzqjtmqwm/imageFileData/MA2ohF?pubver=4',
                productImage: imageUrl
            }
        };
        // Call the createPdf function
        pdfMake.createPdf(docDefinition).download('Product.pdf');
    });
};
//@CUSTOM: render contact hobart message
const rsmRenderContactMessage = (overweightMessage: string): JSX.Element => {
    return (
        <div className={classnames('ms-buybox__message', 'overweight')}>
            {overweightMessage}
        </div>
    );
};
//@CUSTOM: render contact for NLA item
const rsmRenderContactMessageNLA = (NLAMessage: string): JSX.Element => {
    return (
        <div className={classnames('ms-buybox__message', 'nla')}>
            {NLAMessage}
        </div>
    );
};

// @CUSTOM: Shipping message
const rsmDisplayShipsTodayHours = (timeRemaining: number, shippingTime: string): JSX.Element => {
    if (timeRemaining < 0) {
        timeRemaining = 0;
    }
    return (
        <div className={classnames('ms-buybox__message', 'shipping')}>
            {format(shippingTime, timeRemaining)}
        </div>
    );
};
const _renderAddToCart = (addToCart: IBuyboxAddToCartViewProps): JSX.Element => {
    const { ContainerProps, errorBlock, button } = addToCart;

    return (
        <Node {...ContainerProps}>
            {errorBlock}
            {button}
        </Node>
    );
};

const _renderAddToOrderTemplate = (addToOrderTemplate: IBuyboxAddToOrderTemplateViewProps): JSX.Element => {
    const { ContainerProps, errorBlock, button } = addToOrderTemplate;

    return (
        <Node {...ContainerProps}>
            {errorBlock}
            {button}
        </Node>
    );
};

const _renderConfigure = (configure: IBuyboxProductConfigureViewProps): JSX.Element => {
    const { ContainerProps, dropdowns } = configure;

    return <Node {...ContainerProps}>{dropdowns.map(_renderConfigureDropdown)}</Node>;
};

const _renderSocialShare = (socialShare: React.ReactNode[]): JSX.Element | undefined => {
    if (!socialShare || socialShare.length === 0) {
        return undefined;
    }

    return <>{socialShare[0]}</>;
};

const _renderConfigureDropdown = (dropdown: IBuyboxProductConfigureDropdownViewProps): JSX.Element => {
    const { ContainerProps, LabelContainerProps, heading, errors, select } = dropdown;

    return (
        <Node {...ContainerProps}>
            <Node {...LabelContainerProps}>
                {heading}
                {errors}
            </Node>
            {select}
        </Node>
    );
};

const _renderFindInStore = (findInStore: IBuyboxFindInStoreViewProps): JSX.Element => {
    const { ContainerProps, storeSelector, heading, description, errors, button, modal, productPickupOptionList } = findInStore;

    return (
        <Node {...ContainerProps}>
            {storeSelector}
            {heading}
            {productPickupOptionList}
            {description}
            {errors}
            {button}
            {modal}
        </Node>
    );
};

const _renderQuantity = (quantity: IBuyboxProductQuantityViewProps): JSX.Element => {
    const { ContainerProps, LabelContainerProps, heading, input, errors } = quantity;

    return (
        <Node {...ContainerProps}>
            <Node {...LabelContainerProps}>
                {heading}
                {errors}
            </Node>
            {input}
        </Node>
    );
};

const _renderKeyInPrice = (keyInPrice: IBuyboxKeyInPriceViewProps): JSX.Element => {
    const { ContainerProps, LabelContainerProps, heading, input } = keyInPrice;

    return (
        <Node {...ContainerProps}>
            <Node {...LabelContainerProps}>{heading}</Node>
            {input}
        </Node>
    );
};

const _renderShopSimilarItem = (shopSimilarItem: IBuyboxShopSimilarLookViewProps): JSX.Element => {
    const { ContainerProps, errors, input } = shopSimilarItem;

    return (
        <Node {...ContainerProps}>
            {errors}
            {input}
        </Node>
    );
};

export default BuyboxView;
